<template>
  <div
    class="ok-form-input"
    :class="(fromComponent?'from-component':'')"
    @click="$emit('click')"
  >
    <validation-provider
      #default="{ errors, invalid }"
      :name="validationName || label"
      :debounce="100"
      :rules="inputRules"
    >
      <b-form-group
        :label="hideLabel ? '' : label"
        :label-class="{'p-0': hideLabel, [labelClass]: true}"
        :label-cols-xl="labelColsXl"
        :label-cols-lg="labelColsLg"
        :label-cols-md="labelColsMd"
        :label-cols-sm="labelColsSm"
        :label-cols="labelCols"
        :content-cols="contentCols"
        :class="{'mb-0': hideErrors}"
        :state="errors.length ? false : null"
      >
        <template #label>
          <div
            v-if="!hideLabel"
            class="d-flex ok-input-label"
          >
            <div class="one-line">
              <slot name="label">
                {{ hideLabel ? '' : label }}
              </slot>
            </div>

            <ok-tooltip
              :text="tooltip"
              :images="tooltipImages"
              :custom-class="tooltipClass"
            />

            <span
              v-if="isRequired && !hideRequiredAsterisk && !hideLabel"
              class="text-danger required-star"
            >
              *
            </span>
          </div>
        </template>
        <slot
          :errors="errors"
          :invalid="invalid"
        >
          <b-input-group
            :append="append"
            :class="{'input-group-merge': isAppendMerged}"
          >
            <b-form-textarea
              v-if="textarea"
              :value="value"
              :readonly="readOnly"
              :state="errors.length > 0 ? false : null"
              :placeholder="inputPlaceholder"
              :disabled="disabled"
              :style="areaStyle"
              :rows="rows"
              :size="size"
              :max-length="maxLength"
              @input="$emit('input', $event)"
            />
            <b-form-input
              v-else
              :value="value"
              :readonly="readOnly"
              :type="type"
              :min="type === 'number' ? min : null"
              :disabled="disabled"
              :state="errors.length > 0 ? false : null"
              :placeholder="inputPlaceholder"
              :class="inputClass"
              :size="size"
              :dir="dir"
              :max-length="maxLength"
              @input="$emit('input', $event)"
            />

            <b-input-group-append
              v-if="showAppend"
              :is-text="isAppendText"
            >
              <slot name="append" />
            </b-input-group-append>
          </b-input-group>
          <slot name="footer" />
        </slot>
        <small
          v-if="!hideErrors"
          class="text-danger"
        >
          {{ errors[0] }}
        </small>
      </b-form-group>
    </validation-provider>
  </div>
</template>

<script>
import {
  BFormInput, BFormGroup, BInputGroup, BInputGroupAppend, BFormTextarea, VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import '@core/utils/validations/validations'
import isPlainObject from 'lodash/isPlainObject'
import okTextInputProps from '@/@okmenu/components/inputs/utils/ok-text-input-props'
import OkTooltip from '@/@okmenu/components/utils/OkTooltip'

/**
 * General input
 * @displayName OkInput
 */
export default {
  name: 'OkTextInput',
  components: {
    OkTooltip,
    BFormInput,
    BFormGroup,
    ValidationProvider,
    BInputGroup,
    BInputGroupAppend,
    BFormTextarea,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    ...okTextInputProps,
  },
  computed: {
    inputRules() {
      if (!this.isRequired) return this.rules

      if (isPlainObject(this.rules)) {
        return {
          ...this.rules,
          required: true,
        }
      }

      return `required${this.isRequired && this.rules ? '|' : ''}${this.rules || ''}`
    },
    showAppend() {
      return !!this.$scopedSlots.append || !!this.$slots.append
    },
    inputPlaceholder() {
      return this.placeholder ?? this.label
    },
  },
}
</script>

<style lang="scss">
.ok-form-input {
  &:not(.from-component) legend {
    margin: 0;
  }
  legend {
    .required-star {
      padding-left: 3px;
    }

    .feather {
      margin-left: 3px;
    }

    [dir=rtl] & {
      .required-star {
        padding-right: 3px;
        padding-left: 0;
      }

      .feather {
        margin-left: 0;
        margin-right: 3px;
      }
    }
  }
}
</style>
